
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

// Singleton channel management
let sharedChannel: any = null;
let channelSubscribers = new Set<(payload: any) => void>();
let channelRefCount = 0;

const getOrCreateSharedChannel = () => {
  if (!sharedChannel) {
    sharedChannel = supabase
      .channel(`site-config-${Date.now()}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'site_configurations'
      }, (payload) => {
        channelSubscribers.forEach(callback => callback(payload));
      })
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.warn('⚠️ Site config subscription failed');
          // Properly cleanup the channel
          const oldChannel = sharedChannel;
          sharedChannel = null;
          channelSubscribers.clear();
          channelRefCount = 0;
          
          if (oldChannel) {
            setTimeout(() => {
              try {
                supabase.removeChannel(oldChannel);
              } catch (error) {
                console.error('Error removing failed channel:', error);
              }
            }, 100);
          }
        }
      });
  }
  return sharedChannel;
};

export const useSiteConfigValue = (key: string, defaultValue: string = '') => {
  const [value, setValue] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('site_configurations')
          .select('value')
          .eq('key', key)
          .maybeSingle();

        if (error) {
          console.error('Error fetching site config:', error);
          setValue(null);
        } else {
          setValue(data?.value || null);
        }
      } catch (err) {
        console.error('Error:', err);
        setValue(null);
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();

    // Create callback for this specific key
    const updateCallback = (payload: any) => {
      if (payload.new?.key === key) {
        if (payload.eventType === 'UPDATE' || payload.eventType === 'INSERT') {
          setValue(payload.new?.value || null);
        } else if (payload.eventType === 'DELETE') {
          setValue(null);
        }
      }
    };

    // Add subscriber and increment ref count
    channelSubscribers.add(updateCallback);
    channelRefCount++;

    // Initialize shared channel if needed
    getOrCreateSharedChannel();

    return () => {
      // Remove subscriber and decrement ref count
      channelSubscribers.delete(updateCallback);
      channelRefCount--;

      // Clean up channel if no more subscribers
      if (channelRefCount === 0 && sharedChannel) {
        try {
          supabase.removeChannel(sharedChannel);
          sharedChannel = null;
          channelSubscribers.clear();
        } catch (error) {
          console.error('Error cleaning up shared channel:', error);
        }
      }
    };
  }, [key]);

  const refetch = async () => {
    setLoading(true);
    try {
      console.log(`Manual refetch for key: ${key}`);
      const { data, error } = await supabase
        .from('site_configurations')
        .select('value')
        .eq('key', key)
        .maybeSingle();

      if (error) {
        console.error('Error refetching site config:', error);
        setValue(null);
      } else {
        console.log(`Manual refetch result for ${key}:`, data?.value);
        setValue(data?.value || null);
      }
    } catch (err) {
      console.error('Error:', err);
      setValue(null);
    } finally {
      setLoading(false);
    }
  };

  return { value: value || defaultValue, loading, refetch };
};
